// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Tab } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseTabList, BaseToolbar } from '~/components/common';
import { ActivityLogProductTab, ProductAvailabilityTab, ProductDetailTab } from '~/components/module/inventory';
// Others
import { StorageEnum } from '~/utils/enum/base';
import { EMPTY_STRING } from '~/utils/constants/common';
import { IBaseTabItem } from '~/utils/interface/common';
import { InventoryTabEnum, RoleEnum } from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { PRODUCT_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './InventoryDetail.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const InventoryDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canEditProducts } = usePermissions(PRODUCT_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/inventory', 'common/base']);
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { id: inventoryId } = useParams();
  const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;

  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [{ label: InventoryTabEnum.PRODUCT_DETAIL, component: <ProductDetailTab /> }];

    items.push({ label: InventoryTabEnum.ACTIVITY_LOG, component: <ActivityLogProductTab /> });

    items.push({ label: InventoryTabEnum.PRODUCT_AVAILABILITY, component: <ProductAvailabilityTab /> });

    setTabItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>(InventoryTabEnum.PRODUCT_DETAIL);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (hash) setTabKey(hash.slice(1));
  }, [hash]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate(`#${newValue}`);
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };

  const handleOnBack = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.inventory);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.inventory);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.inventory);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.inventory);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.inventory);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.inventory);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.inventory);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.inventory);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.inventory);
        break;
    }
  };

  const handleEdit = () => {
    if (!inventoryId) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateInventory(inventoryId));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateInventory(inventoryId));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateInventory(inventoryId));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateInventory(inventoryId));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateInventory(inventoryId));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateInventory(inventoryId));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateInventory(inventoryId));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateInventory(inventoryId));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateInventory(inventoryId));
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='inventoryDetailPage' className={cx('container')}>
      <BaseToolbar title={t('inventory_detail_title')} isBack onBackToPreviousPage={handleOnBack}>
        {canEditProducts && (
          <Button variant='contained' className={cx('buttonEdit')} onClick={handleEdit}>
            {t('inventory_detail_title_edit_product')}
          </Button>
        )}
      </BaseToolbar>

      <Stack height={5} />

      <BaseTabList value={tabKey} onChange={handleTabChange} type='small'>
        {tabItems.map((item, index) => (
          <Tab
            value={item.label}
            key={index}
            label={t(`tab_label_${item.label}`)}
            className={cx('tabItem', { active: tabKey === item.label })}
          />
        ))}
      </BaseTabList>

      <Box component={'div'} className={cx('body')}>
        <Box component={'div'} className={cx('tabContent')}>
          {renderTabContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default InventoryDetail;
